import React, { useRef, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { ButtonComponent } from 'components/ButtonComponent/ButtonComponent';
import { TextValidatorComponent } from 'components/FormComponents';
import { HtmlTemplateInput } from 'types';
import { useSubmittingState } from 'hooks';
import { getTWithPathKey } from 'utils';

import { Dialog, DialogTitle, DialogContent, DialogActions } from './style';

interface Props {
  open: boolean;
  fields: HtmlTemplateInput[];
  templateName: string;
  onSave: (fields: HtmlTemplateInput[]) => Promise<void>;
  onClose: () => void;
}

export const ChangeThemeFieldsDialog = ({ open, fields, templateName, onSave, onClose }: Props) => {
  const formRef = useRef<ValidatorForm>(null);
  const [initialValues] = useState<HtmlTemplateInput[]>(fields);
  const [inputFields, setInputFields] = useState<HtmlTemplateInput[]>(fields);
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();
  const getT = getTWithPathKey('pages.products.manageProduct.productTheme.modal');
  const getCommonT = getTWithPathKey('common.buttons');

  const onInputChange = (index: number) => (event: Record<string, string>) => {
    setInputFields(prevState =>
      prevState.map((field, i) => ({
        ...field,
        name: index === i ? event[`name-${index}`] : field.name,
      })),
    );
  };

  const handleResetChanges = () => {
    setInputFields(prevState =>
      prevState.map(field => ({
        ...field,
        name: field.identifier,
      })),
    );
  };

  const handleSubmit = () => {
    // submit button submits not only this form but also product details form
    // and any event.stopPropagation, that exists inside ValidatorForm submit func,
    // not prevent from event bubbling.
    if (formRef.current) {
      // @ts-ignore
      formRef.current?.submit();
    }
  };

  const handleSave = async () => {
    await onSave(inputFields);
    onClose();
  };

  const isNamesChanged = inputFields.some((field, index) => field.name !== initialValues[index].name);

  const isDefaultChanges = inputFields.some(i => i.name !== i.identifier);

  return (
    <Dialog onClick={event => event.stopPropagation()} open={open} onClose={onClose} fullWidth>
      <ValidatorForm ref={formRef} onSubmit={() => withHandlingSubmittingState(handleSave)()}>
        <DialogTitle>{getT('title', { templateName })}</DialogTitle>
        <Typography variant="caption">{getT('description')}</Typography>
        <DialogContent>
          {inputFields.map((field, index) => (
            <TextValidatorComponent
              key={field.identifier}
              fieldName={`name-${index}`}
              label={field.identifier}
              value={field.name}
              handleChange={onInputChange(index)}
              validators={[
                {
                  rule: 'required',
                  message: '',
                },
              ]}
            />
          ))}
        </DialogContent>
        <DialogActions>
          {isDefaultChanges && (
            <Box marginLeft="0" marginRight="auto">
              <ButtonComponent
                className="theme-reset"
                variant="text"
                text={getT('reset')}
                onClick={handleResetChanges}
              />
            </Box>
          )}
          <ButtonComponent onClick={onClose} text={getCommonT('cancel')} />
          <ButtonComponent
            onClick={handleSubmit}
            color="primary"
            disabled={!isNamesChanged}
            loading={isSubmitting}
            loaderSize="1.35em"
            text={getCommonT('save')}
          />
        </DialogActions>
      </ValidatorForm>
    </Dialog>
  );
};
